import { useState } from 'react'
import {
  Button,
  Input,
  VStack,
  Text,
  HStack,
  Box,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogBackdrop,
  DialogPositioner,
  IconButton,
} from '@chakra-ui/react'
import { AlertTriangle, Copy, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface ConfirmDeleteDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
  entityName: string // Ex: "Jean DUPONT"
  entityType: string // Ex: "copropriétaire", "lot", "résidence"
  warningMessage?: string // Optional custom warning message
}

export function ConfirmDeleteDialog({
  isOpen,
  onClose,
  onConfirm,
  entityName,
  entityType,
  warningMessage,
}: ConfirmDeleteDialogProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [isCopied, setIsCopied] = useState(false)

  const isValid = inputValue === entityName

  const handleConfirm = async () => {
    if (!isValid) return

    setIsDeleting(true)
    try {
      await onConfirm()
      handleClose()
    } catch (error) {
      console.error('Error during deletion:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleClose = () => {
    setInputValue('')
    setIsCopied(false)
    onClose()
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(entityName)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy:', error)
    }
  }

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && handleClose()}>
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="500px">
          <DialogHeader>
          <DialogTitle display="flex" alignItems="center" gap={2}>
            <AlertTriangle size={24} color="#ef4444" />
            <span>{t('common.confirm_delete')}</span>
          </DialogTitle>
        </DialogHeader>

        <DialogBody>
          <VStack align="stretch" gap={4}>
            <Text>
              {warningMessage || t('common.delete_warning', { type: entityType })}
            </Text>

            {/* Entity name with copy button */}
            <Box
              p={3}
              bg="red.50"
              borderRadius="md"
              borderWidth="1px"
              borderColor="red.200"
            >
              <HStack justify="space-between">
                <Text fontWeight="bold" fontSize="lg" color="red.700">
                  {entityName}
                </Text>
                <IconButton
                  size="sm"
                  variant="ghost"
                  colorPalette={isCopied ? 'green' : 'gray'}
                  onClick={handleCopy}
                  aria-label={t('common.copy')}
                >
                  {isCopied ? <Check size={16} /> : <Copy size={16} />}
                </IconButton>
              </HStack>
            </Box>

            <Text fontSize="sm" color="gray.600">
              {t('common.confirm_delete_instruction')}
            </Text>

            {/* Input field for confirmation */}
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={t('common.type_name_to_confirm')}
              size="lg"
              autoFocus
            />

            {inputValue && !isValid && (
              <Text fontSize="sm" color="red.500">
                {t('common.name_mismatch')}
              </Text>
            )}
          </VStack>
        </DialogBody>

        <DialogFooter>
          <HStack gap={2}>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isDeleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              colorPalette="red"
              onClick={handleConfirm}
              disabled={!isValid || isDeleting}
              loading={isDeleting}
            >
              {t('common.delete')}
            </Button>
          </HStack>
        </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
